import React from 'react';

import { Box, Header } from '@rbilabs/universal-components';
import { ItemIngredients } from 'features/menu/components/product-detail/product-nutrition-information/item-ingredients-information';
import { IngredientsType } from 'features/menu/components/product-detail/product-nutrition-information/types';

import Picture from 'components/picture';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  ItemNutritionInfoToHide,
  defaultItemNutritionInfoToHide,
} from 'state/launchdarkly/variations';

import { Allergens } from '../allergens';
import { NutrientList } from '../nutrient-list';
import { useNutrientItems } from '../use-nutrient-items';
import { TableHeader } from '../v2/table-header';

import { IItemNutritionProps } from './types';
import { getIngredients } from './utils';

export const ItemNutrition: React.FC<React.PropsWithChildren<IItemNutritionProps>> = ({
  itemImage,
  itemName,
  nutritionInfo,
  allergenGroups,
  ingredients,
}) => {
  const { language } = useLocale();

  const normalizedIngredients = ingredients?.map(ingredient =>
    getIngredients(ingredient as any, language)
  ) as IngredientsType[];

  const enableNutritionExplorerAllergens = useFlag(
    LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS
  );
  const itemsFromNutritionInfoToHide =
    useFlag<ItemNutritionInfoToHide>(LaunchDarklyFlag.HIDE_ITEMS_FROM_NUTRITION_INFO) ||
    defaultItemNutritionInfoToHide;

  const nutrients = useNutrientItems(
    (nutritionInfo as unknown) as Record<string, number | null | undefined>,
    itemsFromNutritionInfoToHide
  );

  return (
    <>
      <Picture
        image={itemImage}
        alt="itemImage"
        objectFitContain
        width={300}
        height={300}
        alignSelf="center"
      />
      <Box marginBottom="$4">
        <Header textAlign="center" focusable>
          {itemName}
        </Header>

        <TableHeader testID="per-serving-header" title={t('perServing')} />
        <NutrientList nutrients={nutrients} />

        {ingredients && <ItemIngredients ingredients={normalizedIngredients} />}

        {enableNutritionExplorerAllergens && !!allergenGroups?.length && (
          <>
            <TableHeader testID="allergens-header" title={t('allergenInformation')} />
            <Allergens allergenGroups={allergenGroups} />
          </>
        )}
      </Box>
    </>
  );
};
